import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, FileText, UserCog, Settings as SettingsIcon, ArrowRight, Calendar, Receipt } from "lucide-react";

const stat = (label, value, hint) => ({ label, value, hint });

export default function Dashboard() {
  const [counts, setCounts] = useState({ customers: "—", templates: "—", staff: "—" });

  useEffect(() => {
    Promise.all([
      api.get("/customers").catch(() => ({ data: [] })),
      api.get("/templates").catch(() => ({ data: [] })),
      api.get("/staff").catch(() => ({ data: [] })),
    ]).then(([c, t, s]) => {
      setCounts({
        customers: c.data?.length ?? 0,
        templates: t.data?.length ?? 0,
        staff: s.data?.length ?? 0,
      });
    });
  }, []);

  const stats = [
    stat("Customers", counts.customers, "Active accounts"),
    stat("Job Templates", counts.templates, "Reusable line items"),
    stat("Staff", counts.staff, "On-call operators"),
  ];

  const cards = [
    { to: "/calendar", label: "Calendar", icon: Calendar, desc: "7-day rolling view of bookings", testId: "card-calendar" },
    { to: "/customers", label: "Customers", icon: Users, desc: "Add, edit, search by phone or name", testId: "card-customers" },
    { to: "/accounts", label: "Accounts", icon: Receipt, desc: "Invoices, payment status, CSV export", testId: "card-accounts" },
    { to: "/templates", label: "Job Templates", icon: FileText, desc: "Service packages & defaults", testId: "card-templates" },
    { to: "/staff", label: "Staff", icon: UserCog, desc: "Slugs, PINs, active status", testId: "card-staff" },
    { to: "/settings", label: "Settings", icon: SettingsIcon, desc: "Company, invoice template, API keys, demo reset", testId: "card-settings" },
  ];

  return (
    <div className="page" data-testid="dashboard-page">
      <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// Manager Console</div>
      <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">Dashboard</h1>
      <p className="text-muted-foreground text-sm mt-1">A clean view of your operation.</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
        {stats.map((s) => (
          <div
            key={s.label}
            className="bg-card border border-border rounded-[8px] p-5 grain"
            data-testid={`stat-${s.label.toLowerCase().replace(" ", "-")}`}
          >
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{s.label}</div>
            <div className="text-4xl font-black mt-2">{s.value}</div>
            <div className="text-xs text-muted-foreground mt-1">{s.hint}</div>
          </div>
        ))}
      </div>

      <div className="mt-10">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">// Manage</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
          {cards.map(({ to, label, icon: Icon, desc, testId }) => (
            <Link
              key={to}
              to={to}
              className="group bg-card border border-border hover:border-accent rounded-[8px] p-6 transition-colors flex items-start justify-between gap-4"
              data-testid={testId}
            >
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-[6px] bg-secondary grid place-items-center group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-bold text-base">{label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-accent transition-colors" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
